import { useId, useState, type ReactNode } from 'react';

/**
 * The form primitives.
 *
 * Every constraint the server enforces is enforced here too, in the box itself:
 * a GSTIN box takes fifteen characters and no more, a date box will not accept
 * next year, a percentage box will not accept 150. A field that lets somebody
 * type something the server will reject has wasted their time and taught them
 * to distrust the screen.
 *
 * Nothing here silently corrects what was typed. Weights and amounts are
 * refused a fourth decimal rather than rounded, because in this trade a
 * rounded weight is a real loss.
 */

/**
 * Two fields to a row on anything wider than a phone.
 *
 * A form that runs one field per line down a tall column makes somebody scroll
 * to see what they are filling in, and the shops this is for work on short
 * screens. `wide` is for the ones that genuinely need the room — a name, an
 * address line, a narration.
 */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>;
}

export function Field({
  label,
  hint,
  error,
  required,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="label">
        {label}
        {required ? <span className="ml-0.5 text-danger">*</span> : null}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-2xs text-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-2xs text-ink-faint">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  disabled,
  maxLength,
  uppercase,
  /** Characters the field accepts at all; anything else is simply not typed. */
  allow,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  maxLength?: number;
  uppercase?: boolean;
  allow?: RegExp;
}) {
  return (
    <input
      // Kept away from the browser's autofill: these are weights, rupees and
      // tag numbers, not an address it should remember and offer back.
      autoComplete="off"
      data-lpignore="true"
      data-form-type="other"
      data-1p-ignore="true"
      className="field"
      value={value}
      maxLength={maxLength}
      autoFocus={autoFocus}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => {
        let next = e.target.value;
        if (uppercase) next = next.toUpperCase();
        if (allow) next = next.split('').filter((c) => allow.test(c)).join('');
        onChange(maxLength ? next.slice(0, maxLength) : next);
      }}
    />
  );
}

/**
 * Numbers stay strings the whole way to the server, exactly as stored.
 *
 * `decimals` is the important part: the server takes money at two places,
 * weight at three and fine weight at four, and rejects anything longer. The box
 * refuses the extra digit as it is typed, so the error never happens.
 */
export function NumberInput({
  value,
  onChange,
  placeholder,
  suffix,
  decimals,
  max,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  suffix?: string;
  decimals?: number;
  max?: number;
  disabled?: boolean;
}) {
  const clean = (raw: string): string => {
    const digits = raw.replace(/[^\d.]/g, '');
    const [whole = '', ...rest] = digits.split('.');
    let next = rest.length === 0 ? whole : `${whole}.${rest.join('').slice(0, decimals ?? 6)}`;
    // A cap is a cap: 150% is not a typo to be corrected later.
    if (max !== undefined && next !== '' && Number(next) > max) next = String(max);
    return next;
  };

  return (
    <div className="relative">
      <input
        // Kept away from the browser's autofill: these are weights, rupees and
        // tag numbers, not an address it should remember and offer back.
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        className={`field num ${suffix ? 'pr-9' : ''}`}
        inputMode="decimal"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(clean(e.target.value))}
      />
      {suffix ? (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-2xs text-ink-faint">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}

/** A share, never above a hundred. */
export function PercentInput(props: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return <NumberInput {...props} decimals={2} max={100} suffix="%" />;
}

/** Today on this machine's clock, not UTC — before 5:30 am in India those differ. */
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * A date on a document.
 *
 * Capped at today by default: a shop records what has happened, not what will.
 * A stray keystroke in the year — and these are typed, not picked, by anyone
 * working quickly — otherwise puts an invoice in 2036, where no financial year
 * covers it and the posting fails for a reason nobody can read.
 */
export function DateInput({
  value,
  onChange,
  min,
  max = localToday(),
  allowFuture,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
  allowFuture?: boolean;
}) {
  return (
    <input
      // Kept away from the browser's autofill: these are weights, rupees and
      // tag numbers, not an address it should remember and offer back.
      autoComplete="off"
      data-lpignore="true"
      data-form-type="other"
      data-1p-ignore="true"
      type="date"
      className="field num"
      value={value}
      min={min}
      max={allowFuture ? undefined : max}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Ten digits, as dialled in India. The country code is shown, not typed. */
export function PhoneInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const short = value.length > 0 && value.length < 10;
  return (
    <>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-faint">
          +91
        </span>
        <input
          // Kept away from the browser's autofill: these are weights, rupees and
          // tag numbers, not an address it should remember and offer back.
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore="true"
          className="field num pl-11"
          inputMode="numeric"
          value={value}
          maxLength={10}
          placeholder="98765 43210"
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
        />
      </div>
      {short ? <span className="mt-1 block text-2xs text-danger">A mobile number is ten digits.</span> : null}
    </>
  );
}

const GSTIN_SHAPE = /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d][Z][A-Z\d]$/;

/**
 * Fifteen characters in a fixed shape: state code, PAN, entity, Z, checksum.
 * Telling somebody it is wrong while they type beats a server error after they
 * have filled in the rest of the form.
 */
export function GstinInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const wrong = value.length === 15 && !GSTIN_SHAPE.test(value);
  const partial = value.length > 0 && value.length < 15;
  return (
    <>
      <input
        // Kept away from the browser's autofill: these are weights, rupees and
        // tag numbers, not an address it should remember and offer back.
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        className="field num uppercase"
        value={value}
        maxLength={15}
        placeholder="33ABCDE1234F1Z5"
        onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15))}
      />
      {wrong ? (
        <span className="mt-1 block text-2xs text-danger">That is not the shape of a GSTIN. Check it against their invoice.</span>
      ) : partial ? (
        <span className="mt-1 block text-2xs text-ink-faint">{15 - value.length} characters to go</span>
      ) : null}
    </>
  );
}

const PAN_SHAPE = /^[A-Z]{5}\d{4}[A-Z]$/;

export function PanInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const wrong = value.length === 10 && !PAN_SHAPE.test(value);
  return (
    <>
      <input
        // Kept away from the browser's autofill: these are weights, rupees and
        // tag numbers, not an address it should remember and offer back.
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        className="field num uppercase"
        value={value}
        maxLength={10}
        placeholder="ABCDE1234F"
        onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
      />
      {wrong ? <span className="mt-1 block text-2xs text-danger">A PAN is five letters, four digits, then a letter.</span> : null}
    </>
  );
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <select className="field" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">{placeholder ?? 'Choose…'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * A side panel.
 *
 * It slides rather than appearing, because a panel that arrives without motion
 * reads as the page having been replaced. Escape closes it, the backdrop closes
 * it, and the action stays pinned to the bottom so a long form never hides its
 * own save button.
 */
export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** For a panel that holds a table — a pick list, a document's lines. */
  wide?: boolean;
}) {
  const titleId = useId();
  const [closing, setClosing] = useState(false);

  const close = (): void => {
    setClosing(true);
    setTimeout(onClose, 160);
  };

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
    >
      <div
        className={`absolute inset-0 bg-rail/40 transition-opacity duration-150 ${closing ? 'opacity-0' : 'opacity-100'}`}
        onClick={close}
      />
      <div
        className={`relative flex h-full w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} flex-col bg-canvas shadow-pop transition-transform duration-200 ease-out ${
          closing ? 'translate-x-full' : 'translate-x-0 motion-safe:animate-[slideIn_.2s_ease-out]'
        }`}
      >
        <div className="flex items-start justify-between border-b border-silver-100 bg-surface px-5 py-4">
          <div>
            <h2 id={titleId} className="text-base font-semibold">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 max-w-sm text-sm text-ink-soft">{subtitle}</p> : null}
          </div>
          <button className="btn-quiet -mr-2 px-2 text-2xs" onClick={close}>
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="border-t border-silver-100 bg-surface px-5 py-3">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
